import React, { useState, useEffect, useCallback } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import FlagIcon from "@mui/icons-material/Flag";
import MainLayout from "../Themes/MainLayout";
import { getConfirmDeleteDialog } from "../Components/confirmDeleteDialog";
import {
  Typography,
  Button,
  Alert,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  TextField,
  Menu,
  MenuItem,
  Switch,
  Popover,
  Pagination,
  Autocomplete,
  Popper,
  Tooltip,
} from "@mui/material";
import { Add, Search } from "@mui/icons-material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlined from "@mui/icons-material/EditOutlined";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Table_theme from "../Themes/TableTheme";
import theme from "../Themes/FormThemes";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import { formatDateLocal, formatDateLocal_TF } from "../utils/dateUtils";
import { useCurrentUser } from "../utils/useCurrentUser";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

function EmployeeSalary() {
  const { userId, empId, boId, roles, hasPermission } = useCurrentUser();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [employeeData, setEmployeeData] = useState([]); // Store both employee_id and first_name
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSalary, setSelectedSalary] = useState(null);
  const [formData, setFormData] = useState({
    bo_id: "boId",
    employee_id: "",
    effective_from: "",
    pan_no: "",
    pf_uan_no: "",
    bank_account_no: "",
    insurance: "",
    basic_salary: "",
    hra: "",
    conveyance: "",
    special_allowance: "",
    bonus: "",
    pf_deduction: "",
    tds: "",
    gross_salary: "",
    net_salary: "",
    delete_flag: "N",
    data_source: "Manual",
    created_by: "empId",
    modified_by: "empId",
    remarks: "",
  });
  const [filters, setFilters] = useState({
    bo_id: "",
    first_name: "",
    last_name: "",
    salary_id: "",
    effective_from: "",
    pan_no: "",
    pf_uan_no: "",
    bank_account_no: "",
    insurance: "",
    basic_salary: "",
    hra: "",
    conveyance: "",
    special_allowance: "",
    bonus: "",
    pf_deduction: "",
    tds: "",
    gross_salary: "",
    net_salary: "",
    delete_flag: "",
    data_source: "",
    created_by: "",
    modified_by: "",
    status: "ACTIVE",
    remarks: "",
  });
  const [visibleColumns, setVisibleColumns] = useState({
    employee_id: true, // Will display first_name as "Employee Name"
    effective_from: true,
    pan_no: true,
    pf_uan_no: true,
    bank_account_no: true,
    insurance: true,
    basic_salary: true,
    hra: true,
    conveyance: true,
    special_allowance: true,
    bonus: true,
    pf_deduction: true,
    tds: true,
    gross_salary: true,
    net_salary: true,
    remarks: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [editSalaryId, setEditSalaryId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterAnchorEls, setFilterAnchorEls] = useState({});
  const [tempFilters, setTempFilters] = useState({ ...filters });
  const columnLabels = {
    employee_id: "Employee Name",
    effective_from: "Effective From",
    pan_no: "PAN No",
    pf_uan_no: "PF UAN No",
    bank_account_no: "Bank Account No",
    insurance: "Insurance",
    basic_salary: "Basic Salary",
    hra: "HRA",
    conveyance: "Conveyance",
    special_allowance: "Special Allowance",
    bonus: "Bonus",
    pf_deduction: "PF Deduction",
    tds: "TDS",
    gross_salary: "Gross Salary",
    net_salary: "Net Salary",
    delete_flag: "Delete Flag",
    data_source: "Data Source",
    created_by: "Created By",
    modified_by: "Modified By",
    remarks: "Remarks",
  };

  const fetchEmployeeIds = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}/employee_salaries/ids/${boId}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error("Failed to fetch employee data");
      const data = await res.json();
      setEmployeeData(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching employee data:", err.message);
      setErrorMessage("Failed to load employee data. Please try again.");
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployeeIds();
  }, [fetchEmployeeIds]);

  const fetchSalaries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        boId,
      });
      Object.entries(filters).forEach(([key, value]) => {
        if (!value || String(value).trim() === "") return;
        const formattedValue = String(value).trim();
        params.append(key, formattedValue);
      });
      if (searchTerm && searchTerm.trim() !== "") {
        const trimmedSearch = searchTerm.trim();
        params.append("search", trimmedSearch);
      }
      const response = await fetch(
        `${BASE_URL}/employee_salaries/paginate/list?${params.toString()}`,
        { method: "GET", headers: { "Content-Type": "application/json" } }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch salaries: ${errorText || "Server error"}`
        );
      }
      const result = await response.json();
      if (
        !result.data ||
        !Array.isArray(result.data) ||
        typeof result.total !== "number"
      ) {
        throw new Error("Invalid response format from server");
      }
      setData(result.data);
      setTotal(result.total);
    } catch (err) {
      console.error("Error fetching salaries:", err.message);
      setData([]);
      setTotal(0);
      setErrorMessage(
        err.message || "Failed to load salaries. Please try again."
      );
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters, searchTerm, boId]);

  useEffect(() => {
    fetchSalaries();
  }, [fetchSalaries]);

  useEffect(() => {
    setPage(0);
  }, [filters, searchTerm]);

  const handleFilterIconClick = (event, column) => {
    setFilterAnchorEls((prev) => ({ ...prev, [column]: event.currentTarget }));
  };

  const handleFilterClose = (column) => {
    setFilterAnchorEls((prev) => ({ ...prev, [column]: null }));
  };

  const applyFilter = (column) => {
    setFilters((prev) => ({ ...prev, [column]: tempFilters[column] }));
    handleFilterClose(column);
    setPage(1);
  };

  const clearFilter = (column) => {
    setTempFilters((prev) => ({ ...prev, [column]: "" }));
    setFilters((prev) => ({ ...prev, [column]: "" }));
    handleFilterClose(column);
    setPage(1);
  };

  const handleChange = (nameOrEvent, value) => {
    let name, newValue;
    if (typeof nameOrEvent === "string") {
      name = nameOrEvent;
      newValue = value;
    } else if (nameOrEvent && nameOrEvent.target) {
      ({ name, value: newValue } = nameOrEvent.target);
    } else {
      console.warn("Unexpected handleChange input:", nameOrEvent, value);
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: newValue }));
    setFormErrors((prev) => {
      const updated = { ...prev };
      if (!newValue || String(newValue).trim() === "") {
        updated[name] = "This field is required";
      } else {
        delete updated[name];
      }
      return updated;
    });
  };

  const validate = () => {
    const errors = {};
    const requiredFields = [
      "employee_id",
      "effective_from",
      "pan_no",
      "pf_uan_no",
      "bank_account_no",
      "basic_salary",
      "net_salary",
    ];
    const numericFields = [
      "insurance",
      "basic_salary",
      "hra",
      "conveyance",
      "special_allowance",
      "bonus",
      "pf_deduction",
      "tds",
      "gross_salary",
      "net_salary",
    ];
    const MAX_VALUE = 99999999.99; // NUMERIC(10,2)

    requiredFields.forEach((field) => {
      if (!formData[field] || String(formData[field]).trim() === "") {
        errors[field] = "This field is required";
      }
    });

    numericFields.forEach((field) => {
      const value = formData[field]; // DEFINE value
      if (value === "" || value === null || value === undefined) return;
      const num = Number(value);
      if (Number.isNaN(num)) {
        errors[field] = "This field must be a valid number";
      } else if (num < 0) {
        errors[field] = "Negative values are not allowed";
      } else if (num > MAX_VALUE) {
        errors[field] = `Value exceeds allowed limit (${MAX_VALUE})`;
      }
    });

    if (!formData.effective_from) {
      errors.effective_from = "This field is required";
    } else {
      const formatted = formatDateLocal_TF(formData.effective_from);
      if (!formatted) {
        errors.effective_from = "Invalid date format (use YYYY-MM-DD)";
      }
    }

    if (
      !isEditing &&
      !employeeData.some((emp) => emp.emp_id === formData.employee_id)
    ) {
      errors.employee_id = "Please select the employee.";
    }

    return errors;
  };
  useEffect(() => {
    const fetchStatusFlags = async () => {
      try {
        const res = await fetch(`${BASE_URL}/commonVariables/statusFlag`);
        const data = await res.json();
        setStatusOptions(data); // [{ code: "ACTIVE", label: "Active" }, ...]
        // Set default to ACTIVE if available
        const activeOption = data.find((option) => option.code === "ACTIVE");
        if (activeOption) {
          setSelectedStatus(activeOption.code);
          setFilters((prev) => ({ ...prev, status: activeOption.code }));
        }
      } catch (err) {
        console.error("Failed to fetch employee status flags:", err);
      }
    };

    fetchStatusFlags();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }

    const payload = {
      bo_id: formData.bo_id,
      employee_id: formData.employee_id,
      effective_from: formData.effective_from
        ? formatDateLocal_TF(formData.effective_from)
        : null,
      pan_no: formData.pan_no,
      pf_uan_no: formData.pf_uan_no || "",
      bank_account_no: formData.bank_account_no,
      insurance: parseFloat(formData.insurance) || 0.0,
      basic_salary: parseFloat(formData.basic_salary),
      hra: parseFloat(formData.hra) || 0.0,
      conveyance: parseFloat(formData.conveyance) || 0.0,
      special_allowance: parseFloat(formData.special_allowance) || 0.0,
      bonus: parseFloat(formData.bonus) || 0.0,
      pf_deduction: parseFloat(formData.pf_deduction) || 0.0,
      tds: parseFloat(formData.tds) || 0.0,
      gross_salary: parseFloat(formData.gross_salary),
      net_salary: parseFloat(formData.net_salary),
      delete_flag: formData.delete_flag || "N",
      data_source: formData.data_source || "Manual",
      created_by: isEditing ? formData.created_by : empId,
      modified_by: empId,
      remarks: formData.remarks || "",
    };

    try {
      const url = isEditing
        ? `${BASE_URL}/employee_salaries/${boId}/${editSalaryId}`
        : `${BASE_URL}/employee_salaries`;
      const method = isEditing ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let errorMessage = "Failed to save salary";

        try {
          const errorData = await res.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = "Unexpected server error";
        }

        throw new Error(errorMessage);
      }

      setSuccessMessage(
        isEditing
          ? "Salary updated successfully!"
          : "Salary created successfully!"
      );
      resetFormData();
      await fetchSalaries();
      setTimeout(() => setSuccessMessage(""), 5000);
      handleDialogClose();
    } catch (error) {
      setErrorMessage(error.message);
      setTimeout(() => setErrorMessage(""), 5000);
      handleDialogClose();
    }
  };

  const resetFormData = () => {
    setFormData({
      bo_id: boId,
      employee_id: "",
      effective_from: "",
      pan_no: "",
      pf_uan_no: "",
      bank_account_no: "",
      insurance: "",
      basic_salary: "",
      hra: "",
      conveyance: "",
      special_allowance: "",
      bonus: "",
      pf_deduction: "",
      tds: "",
      gross_salary: "",
      net_salary: "",
      delete_flag: "N",
      data_source: "Manual",
      created_by: empId,
      modified_by: empId,
      remarks: "",
    });
    setIsEditing(false);
    setEditSalaryId(null);
    setFormErrors({});
  };

  const handleEdit = (salary) => {
    setFormData({
      salary_id: salary.salary_id,
      bo_id: salary.bo_id || "",
      employee_id: salary.employee_id || "",
      effective_from: salary.effective_from
        ? new Date(salary.effective_from)
        : null,
      pan_no: salary.pan_no || "",
      pf_uan_no: salary.pf_uan_no || "",
      bank_account_no: salary.bank_account_no || "",
      insurance: salary.insurance || "",
      basic_salary: salary.basic_salary || "",
      hra: salary.hra || "",
      conveyance: salary.conveyance || "",
      special_allowance: salary.special_allowance || "",
      bonus: salary.bonus || "",
      pf_deduction: salary.pf_deduction || "",
      tds: salary.tds || "",
      gross_salary: salary.gross_salary || "",
      net_salary: salary.net_salary || "",
      delete_flag: salary.delete_flag || "N",
      data_source: salary.data_source || "Manual",
      created_by: salary.created_by || empId,
      modified_by: empId,
      remarks: salary.remarks || "",
    });
    setIsEditing(true);
    setEditSalaryId(salary.salary_id);
    setOpenDialog(true);
  };

  const handleDelete = async (salary) => {
    if (!salary || !salary.salary_id) {
      console.error("Invalid salary object passed to delete handler");
      return;
    }
    try {
      const res = await fetch(
        `${BASE_URL}/employee_salaries/${boId}/${salary.salary_id}`,
        {
          method: "DELETE",
        }
      );
      if (!res.ok) throw new Error("Failed to delete salary");
      const data = await res.json();
      const employeeName = data.employee_name || "Unknown Employee";
      setSuccessMessage(`${employeeName} was deleted successfully!`);
      fetchSalaries();
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      console.error("Error deleting salary:", error);
      setErrorMessage("Failed to delete salary. Please try again.");
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const confirmDelete = (salary) => {
    if (!salary || !salary.salary_id) {
      console.error("Invalid salary object passed to delete handler");
      return;
    }

    setSelectedSalary(salary);
    setDeleteDialogOpen(true);
  };

  const handleDialogOpen = () => {
    resetFormData();
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleToggleFlag = async (salary_id, delete_flag) => {
    try {
      const newFlag = delete_flag === "N" ? "Y" : "N";
      const res = await fetch(
        `${BASE_URL}/employee_salaries/${boId}/${salary_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            delete_flag: newFlag,
            bo_id: boId,
            modified_by: empId,
          }),
        }
      );
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(
          `Failed to toggle delete flag: ${errorText || res.statusText}`
        );
      }
      const updatedSalary = await res.json();
      setData((prevData) =>
        prevData.map((item) =>
          item.salary_id === salary_id
            ? { ...item, ...updatedSalary.salary }
            : item
        )
      );
      setSuccessMessage(`Toggle flag updated Successfully..`);
      fetchSalaries();
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      console.error("Error toggling delete flag:", error);
      setErrorMessage(
        "Failed to toggle salary status. Check console for details."
      );
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const columns = Object.keys(visibleColumns);

  return (
    <MainLayout>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        gap={1.5}
        mb={2}
      >
        <Button
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ minWidth: 0, p: 1 }}
        >
          <SettingsIcon />
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              mt: 1,
              p: 1,
              width: 250,
              maxHeight: 300,
              overflowY: "auto",
              borderRadius: 2,
            },
          }}
        >
          {columns.map((col) => (
            <MenuItem key={col} disableGutters sx={{ p: 0, mb: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  border: "1px solid #ccc",
                  borderRadius: 1.5,
                  px: 2,
                  py: 1,
                  backgroundColor: "#fff",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    color: "#333",
                    fontWeight: "Bold",
                  }}
                >
                  {columnLabels[col] || col}
                </Typography>
                <Switch
                  size="small"
                  checked={visibleColumns[col]}
                  onChange={(e) =>
                    setVisibleColumns((prev) => ({
                      ...prev,
                      [col]: e.target.checked,
                    }))
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#2DB0E4" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#2DB0E4",
                    },
                  }}
                />
              </Box>
            </MenuItem>
          ))}
        </Menu>
        {hasPermission("Employee Salaries", "Edit") && (
          <Button
            variant="contained"
            onClick={handleDialogOpen}
            color="primary"
            sx={{
              fontWeight: "bold",
              borderRadius: "8px",
              px: 2.5,
              height: 36,
              backgroundColor: "#2DB0E4",
            }}
            startIcon={<Add />}
          >
            Add
          </Button>
        )}
        <TextField
          size="small"
          sx={{
            width: 250,
            "& input": { fontSize: 14 }, // input + placeholder
            "& input::placeholder": { fontSize: 12 }, // placeholder only
          }}
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          InputProps={{
            sx: { height: 36, paddingRight: 0 },
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {/* Status Dropdown */}
        <TextField
          select
          size="small"
          label="Status"
          value={filters.status || ""}
          onChange={(e) =>
            setFilters((prev) => ({ ...prev, status: e.target.value }))
          }
          sx={{
            width: 200,

            "& .MuiInputBase-root": {
              height: 36,
            },

            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center",
              fontSize: 12,
              padding: "0 14px",
            },

            "& .MuiInputLabel-root": { fontSize: "14px" },
          }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  "& .MuiMenuItem-root": {
                    fontSize: "14px",
                    minHeight: "32px",
                  },
                },
              },
            },
          }}
        >
          <MenuItem value="ALL">All</MenuItem>
          {statusOptions.map((opt) => (
            <MenuItem key={opt.code} value={opt.code}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box sx={{ width: "100%", overflowX: "auto", px: 0, py: 2, mt: "-10px" }}>
        {getConfirmDeleteDialog({
          open: deleteDialogOpen,
          onClose: () => {
            setDeleteDialogOpen(false);
            setSelectedSalary(null);
          },
          onConfirm: () => {
            handleDelete(selectedSalary);
            setDeleteDialogOpen(false);
            setSelectedSalary(null);
          },
          title: "Confirm Deletion",
          contentText: selectedSalary
            ? `Are you sure you want to delete Employee ${selectedSalary.employee_name}?`
            : "Are you sure you want to delete this Employee?",
        })}
        <ThemeProvider theme={Table_theme}>
          <CssBaseline />
          <TableContainer>
            <Table
              sx={{
                "& .MuiTableCell-root": {
                  padding: "18px 36px",
                  fontSize: "12px",
                  lineHeight: 2.4,
                },
                "& .MuiTableCell-head": {
                  padding: "18px 36px",
                  fontWeight: 600,
                },

                "& .MuiIconButton-root": {
                  padding: "4px",
                  "& svg": {
                    fontSize: "16px",
                  },
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Actions</TableCell>
                  {columns.map((column) =>
                    visibleColumns[column] ? (
                      <TableCell key={column}>
                        <Box display="flex" alignItems="center">
                          {columnLabels[column] || column}
                          <IconButton
                            size="small"
                            onClick={(e) => handleFilterIconClick(e, column)}
                          >
                            <FilterListIcon fontSize="small" />
                          </IconButton>
                          <Popover
                            open={Boolean(filterAnchorEls[column])}
                            anchorEl={filterAnchorEls[column]}
                            onClose={() => handleFilterClose(column)}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "left",
                            }}
                          >
                            <Box p={2}>
                              <TextField
                                size="small"
                                value={tempFilters[column] || ""}
                                onChange={(e) =>
                                  setTempFilters((prev) => ({
                                    ...prev,
                                    [column]: e.target.value,
                                  }))
                                }
                                label={`Filter ${
                                  columnLabels[column] || column
                                }`}
                                fullWidth
                              />
                              <Box
                                mt={1}
                                display="flex"
                                justifyContent="space-between"
                              >
                                <Button
                                  onClick={() => applyFilter(column)}
                                  variant="contained"
                                  size="small"
                                >
                                  Apply
                                </Button>
                                <Button
                                  onClick={() => clearFilter(column)}
                                  color="secondary"
                                  size="small"
                                >
                                  Clear
                                </Button>
                              </Box>
                            </Box>
                          </Popover>
                        </Box>
                      </TableCell>
                    ) : null
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : data.length > 0 ? (
                  data.map((salary) => (
                    <TableRow key={salary.salary_id}>
                      <TableCell>
                        <Tooltip
                          title={
                            hasPermission("Employee Salaries", "Edit")
                              ? "Edit"
                              : "No permission"
                          }
                        >
                          <span>
                            <IconButton
                              onClick={() =>
                                hasPermission("Employee Salaries", "Edit") &&
                                handleEdit(salary)
                              }
                              disabled={
                                !hasPermission("Employee Salaries", "Edit")
                              }
                              sx={{
                                color: hasPermission(
                                  "Employee Salaries",
                                  "Edit"
                                )
                                  ? "#2DB0E4"
                                  : "#ccc",
                              }}
                            >
                              <EditOutlined sx={{ fontSize: "16px" }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            hasPermission("Employee Salaries", "Delete")
                              ? "Delete"
                              : "No permission"
                          }
                        >
                          <span>
                            <IconButton
                              onClick={() =>
                                hasPermission("Employee Salaries", "Delete") &&
                                confirmDelete(salary)
                              }
                              disabled={
                                !hasPermission("Employee Salaries", "Delete")
                              }
                            >
                              <DeleteIcon
                                sx={{
                                  color: hasPermission(
                                    "Employee Salaries",
                                    "Delete"
                                  )
                                    ? "red"
                                    : "#ccc",
                                  width: 15,
                                  height: 15,
                                }}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip
                          title={
                            hasPermission("Employee Salaries", "Edit")
                              ? "Toggle Delete Flag"
                              : "No permission"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() =>
                                hasPermission("Employee Salaries", "Edit") &&
                                handleToggleFlag(
                                  salary.salary_id,
                                  salary.delete_flag
                                )
                              }
                              disabled={
                                !hasPermission("Employee Salaries", "Edit")
                              }
                            >
                              <FlagIcon
                                style={{
                                  color:
                                    salary.delete_flag === "N"
                                      ? "#2E9F50"
                                      : "#FF0C0C",
                                  transition: "color 0.2s",
                                  width: 17,
                                  height: 17,
                                  verticalAlign: "middle",
                                }}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                      {visibleColumns.employee_id && (
                        <TableCell>{salary.employee_name}</TableCell>
                      )}
                      {visibleColumns.effective_from && (
                        <TableCell>
                          {formatDateLocal(salary.effective_from)}
                        </TableCell>
                      )}
                      {visibleColumns.pan_no && (
                        <TableCell>{salary.pan_no}</TableCell>
                      )}
                      {visibleColumns.pf_uan_no && (
                        <TableCell>{salary.pf_uan_no}</TableCell>
                      )}
                      {visibleColumns.bank_account_no && (
                        <TableCell>{salary.bank_account_no}</TableCell>
                      )}
                      {visibleColumns.insurance && (
                        <TableCell>{salary.insurance}</TableCell>
                      )}
                      {visibleColumns.basic_salary && (
                        <TableCell>{salary.basic_salary}</TableCell>
                      )}
                      {visibleColumns.hra && (
                        <TableCell>{salary.hra}</TableCell>
                      )}
                      {visibleColumns.conveyance && (
                        <TableCell>{salary.conveyance}</TableCell>
                      )}
                      {visibleColumns.special_allowance && (
                        <TableCell>{salary.special_allowance}</TableCell>
                      )}
                      {visibleColumns.bonus && (
                        <TableCell>{salary.bonus}</TableCell>
                      )}
                      {visibleColumns.pf_deduction && (
                        <TableCell>{salary.pf_deduction}</TableCell>
                      )}
                      {visibleColumns.tds && (
                        <TableCell>{salary.tds}</TableCell>
                      )}
                      {visibleColumns.gross_salary && (
                        <TableCell>{salary.gross_salary}</TableCell>
                      )}
                      {visibleColumns.net_salary && (
                        <TableCell>{salary.net_salary}</TableCell>
                      )}
                      {visibleColumns.delete_flag && (
                        <TableCell>{salary.delete_flag}</TableCell>
                      )}
                      {visibleColumns.data_source && (
                        <TableCell>{salary.data_source}</TableCell>
                      )}
                      {visibleColumns.created_by && (
                        <TableCell>{salary.created_by}</TableCell>
                      )}
                      {visibleColumns.modified_by && (
                        <TableCell>{salary.modified_by}</TableCell>
                      )}
                      {visibleColumns.remarks && (
                        <TableCell>{salary.remarks}</TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} align="center">
                      No salaries found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={Math.ceil(total / rowsPerPage)}
              page={page + 1}
              onChange={(_, newPage) => setPage(newPage - 1)}
              color="primary"
              variant="outlined"
              shape="rounded"
              sx={{
                "& .MuiPaginationItem-root": {
                  borderRadius: "8px",
                  minWidth: "32px",
                  height: "32px",
                  fontSize: "14px",
                },
                "& .Mui-selected": {
                  backgroundColor: "#b3dbea !important",
                  color: "black",
                  fontWeight: "bold",
                },
                "& .MuiPaginationItem-ellipsis": {
                  display: "none",
                },
              }}
            />
          </Box>
        </ThemeProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Dialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            maxWidth={false}
            sx={{
              "& .MuiDialog-paper": {
                borderRadius: 5,
                width: "550px",
                overflow: "visible",
                backgroundColor: "#fff",
              },
            }}
          >
            <DialogTitle
              sx={{
                borderRadius: "12px",
                bgcolor: "#f5f5f5",
                p: 1,
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              {isEditing ? "Edit Salary" : "Add Salary"}
              <IconButton
                aria-label="close"
                onClick={() => setOpenDialog(false)}
                sx={{ position: "absolute", right: 8, top: 5 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent
              sx={{
                p: "10px 24px 22px 24px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                overflowX: "hidden",
                boxSizing: "border-box",
              }}
            >
              <Grid
                container
                spacing={1}
                justifyContent="center"
                sx={{ width: "100%", p: 1, mt: 1 }}
              >
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    id="employee_id"
                    options={employeeData}
                    getOptionLabel={(option) =>
                      option
                        ? `${option.emp_id} - ${option.first_name || ""} ${
                            option.last_name || ""
                          }`.trim()
                        : ""
                    }
                    value={
                      employeeData.find(
                        (e) => e.emp_id === formData.employee_id
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      const newId = newValue ? newValue.emp_id : "";
                      setFormData((prev) => ({
                        ...prev,
                        employee_id: newId,
                      }));
                      setFormErrors((prev) => {
                        const updated = { ...prev };
                        if (!newId) {
                          updated.employee_id = "This field is required";
                        } else {
                          delete updated.employee_id;
                        }
                        return updated;
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Employee Name"
                        required
                        margin="none"
                        InputLabelProps={{ shrink: true }}
                        error={!!formErrors.employee_id}
                        helperText={formErrors.employee_id}
                        sx={{
                          width: "230px",
                          fontSize: "12px",
                          "& .MuiInputBase-root": { minHeight: 35 },
                          "& .MuiInputLabel-root": { fontSize: "12px" },
                          "& .MuiFormHelperText-root": { fontSize: "12px" },
                          "& legend": {
                            maxWidth: "90px",
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {`${option.emp_id} - ${option.first_name || ""} ${
                            option.last_name || ""
                          }`.trim()}
                        </Typography>
                      </li>
                    )}
                    PopperComponent={(props) => (
                      <Popper
                        {...props}
                        sx={{
                          minWidth: "260px",
                          maxHeight: "200px",
                          overflow: "auto",
                        }}
                      />
                    )}
                    ListboxComponent={(props) => (
                      <ul
                        {...props}
                        style={{ maxHeight: "200px", overflow: "auto" }}
                      />
                    )}
                    disabled={employeeData.length === 0 || loading}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Effective From"
                      required
                      value={formData.effective_from || null}
                      onChange={(newValue) => {
                        setFormData((prev) => ({
                          ...prev,
                          effective_from: newValue,
                        }));
                        setFormErrors((prev) => {
                          const updated = { ...prev };
                          if (!newValue) {
                            updated.effective_from = "This field is required";
                          } else {
                            delete updated.effective_from;
                          }
                          return updated;
                        });
                      }}
                      format="MM/dd/yyyy"
                      slotProps={{
                        textField: {
                          required: true,
                          name: "effective_from",
                          placeholder: "MM/DD/YYYY",
                          InputLabelProps: { shrink: true },
                          InputProps: {
                            sx: {
                              paddingTop: "2px",
                              paddingBottom: "2px",
                              fontSize: "0.8rem",
                              height: 35,
                              "& .MuiInputLabel-root": {
                                fontSize: "0.8rem",
                                color: "#000000",
                              },
                              backgroundColor: "#fafafa",
                            },
                          },
                          sx: {
                            width: "230px",

                            "& .MuiInputBase-root": {
                              minHeight: 35,
                              fontSize: "12px",
                              backgroundColor: "#fafafa",
                              color: "#000000",
                            },
                            "& .MuiInputLabel-root": {
                              fontSize: "12px",

                              px: "2px",
                            },
                            "& .MuiFormHelperText-root": {
                              fontSize: "12px",
                            },
                            "& legend": {
                              maxWidth: "95px",
                            },
                          },
                          margin: "none",
                          error: !!formErrors.effective_from,
                          helperText: formErrors.effective_from || "",
                        },
                        popper: {
                          disablePortal: true,
                          placement: "bottom-start",
                          sx: {
                            "& .MuiPaper-root.MuiPickersPopper-paper": {
                              width: "fit-content !important",
                              height: "fit-content !important",
                              overflow: "hidden",
                              padding: 0,
                              minWidth: "unset",
                              minHeight: "unset",
                            },
                            "& .MuiPickersBasePicker-root": {
                              padding: 0,
                              margin: 0,
                              minWidth: "unset",
                              minHeight: "unset",
                            },
                            "& .MuiDayCalendar-root": {
                              padding: 0,
                              margin: 0,
                            },
                            "& .MuiPickersCalendarHeader-root": {
                              padding: "30px",
                              fontSize: "0.7rem",
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="PAN No"
                    required
                    name="pan_no"
                    value={formData.pan_no}
                    onChange={handleChange}
                    fullWidth
                    margin="none"
                    InputLabelProps={{ shrink: true }}
                    error={!!formErrors.pan_no}
                    helperText={formErrors.pan_no}
                    sx={{
                      width: "230px",
                      fontSize: "0.85rem",
                      "& .MuiInputBase-root": { minHeight: 35 },
                      "& .MuiInputLabel-root": { fontSize: "0.8rem" },
                      "& legend": {
                        maxWidth: "50px",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="PF UAN No"
                    required
                    name="pf_uan_no"
                    value={formData.pf_uan_no}
                    onChange={handleChange}
                    fullWidth
                    margin="none"
                    InputLabelProps={{ shrink: true }}
                    error={!!formErrors.pf_uan_no}
                    helperText={formErrors.pf_uan_no}
                    sx={{
                      width: "230px",
                      fontSize: "0.85rem",
                      "& .MuiInputBase-root": { minHeight: 35 },
                      "& .MuiInputLabel-root": { fontSize: "0.8rem" },
                      "& legend": {
                        maxWidth: "65px",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Bank Account No"
                    required
                    name="bank_account_no"
                    value={formData.bank_account_no}
                    onChange={handleChange}
                    fullWidth
                    margin="none"
                    InputLabelProps={{ shrink: true }}
                    error={!!formErrors.bank_account_no}
                    helperText={formErrors.bank_account_no}
                    sx={{
                      width: "230px",
                      fontSize: "0.85rem",
                      "& .MuiInputBase-root": { minHeight: 35 },
                      "& .MuiInputLabel-root": { fontSize: "0.8rem" },
                      "& legend": {
                        maxWidth: "95px",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Insurance"
                    name="insurance"
                    type="number"
                    value={formData.insurance}
                    onChange={handleChange}
                    fullWidth
                    margin="none"
                    InputLabelProps={{ shrink: true }}
                    error={!!formErrors.insurance}
                    helperText={formErrors.insurance}
                    sx={{
                      width: "230px",
                      fontSize: "0.85rem",
                      "& .MuiInputBase-root": { minHeight: 35 },
                      "& .MuiInputLabel-root": { fontSize: "0.8rem" },
                      "& legend": {
                        maxWidth: "55px",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Basic Salary"
                    required
                    name="basic_salary"
                    type="number"
                    value={formData.basic_salary}
                    onChange={handleChange}
                    fullWidth
                    margin="none"
                    InputLabelProps={{ shrink: true }}
                    error={!!formErrors.basic_salary}
                    helperText={formErrors.basic_salary}
                    sx={{
                      width: "230px",
                      fontSize: "0.85rem",
                      "& .MuiInputBase-root": { minHeight: 35 },
                      "& .MuiInputLabel-root": { fontSize: "0.8rem" },
                      "& legend": {
                        maxWidth: "70px",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="HRA"
                    name="hra"
                    type="number"
                    value={formData.hra}
                    onChange={handleChange}
                    fullWidth
                    margin="none"
                    InputLabelProps={{ shrink: true }}
                    error={!!formErrors.hra}
                    helperText={formErrors.hra}
                    sx={{
                      width: "230px",
                      fontSize: "0.85rem",
                      "& .MuiInputBase-root": { minHeight: 35 },
                      "& .MuiInputLabel-root": { fontSize: "0.8rem" },
                      "& legend": {
                        maxWidth: "30px",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Conveyance"
                    name="conveyance"
                    type="number"
                    value={formData.conveyance}
                    onChange={handleChange}
                    fullWidth
                    margin="none"
                    InputLabelProps={{ shrink: true }}
                    error={!!formErrors.conveyance}
                    helperText={formErrors.conveyance}
                    sx={{
                      width: "230px",
                      fontSize: "0.85rem",
                      "& .MuiInputBase-root": { minHeight: 35 },
                      "& .MuiInputLabel-root": { fontSize: "0.8rem" },
                      "& legend": {
                        maxWidth: "65px",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Special Allowance"
                    name="special_allowance"
                    type="number"
                    value={formData.special_allowance}
                    onChange={handleChange}
                    fullWidth
                    margin="none"
                    InputLabelProps={{ shrink: true }}
                    error={!!formErrors.special_allowance}
                    helperText={formErrors.special_allowance}
                    sx={{
                      width: "230px",
                      fontSize: "0.85rem",
                      "& .MuiInputBase-root": { minHeight: 35 },
                      "& .MuiInputLabel-root": { fontSize: "0.8rem" },
                      "& legend": {
                        maxWidth: "95px",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Bonus"
                    name="bonus"
                    type="number"
                    value={formData.bonus}
                    onChange={handleChange}
                    fullWidth
                    margin="none"
                    InputLabelProps={{ shrink: true }}
                    error={!!formErrors.bonus}
                    helperText={formErrors.bonus}
                    sx={{
                      width: "230px",
                      fontSize: "0.85rem",
                      "& .MuiInputBase-root": { minHeight: 35 },
                      "& .MuiInputLabel-root": { fontSize: "0.8rem" },
                      "& legend": {
                        maxWidth: "40px",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="PF Deduction"
                    name="pf_deduction"
                    type="number"
                    value={formData.pf_deduction}
                    onChange={handleChange}
                    fullWidth
                    margin="none"
                    InputLabelProps={{ shrink: true }}
                    error={!!formErrors.pf_deduction}
                    helperText={formErrors.pf_deduction}
                    sx={{
                      width: "230px",
                      fontSize: "0.85rem",
                      "& .MuiInputBase-root": { minHeight: 35 },
                      "& .MuiInputLabel-root": { fontSize: "0.8rem" },
                      "& legend": {
                        maxWidth: "75px",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="TDS"
                    name="tds"
                    type="number"
                    value={formData.tds}
                    onChange={handleChange}
                    fullWidth
                    margin="none"
                    InputLabelProps={{ shrink: true }}
                    error={!!formErrors.tds}
                    helperText={formErrors.tds}
                    sx={{
                      width: "230px",
                      fontSize: "0.85rem",
                      "& .MuiInputBase-root": { minHeight: 35 },
                      "& .MuiInputLabel-root": { fontSize: "0.8rem" },
                      "& legend": {
                        maxWidth: "30px",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Gross Salary"
                    name="gross_salary"
                    type="number"
                    value={formData.gross_salary}
                    onChange={handleChange}
                    fullWidth
                    margin="none"
                    InputLabelProps={{ shrink: true }}
                    error={!!formErrors.gross_salary}
                    helperText={formErrors.gross_salary}
                    sx={{
                      width: "230px",
                      fontSize: "0.85rem",
                      "& .MuiInputBase-root": { minHeight: 35 },
                      "& .MuiInputLabel-root": { fontSize: "0.8rem" },
                      "& legend": {
                        maxWidth: "70px",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Net Salary"
                    required
                    name="net_salary"
                    type="number"
                    value={formData.net_salary}
                    onChange={handleChange}
                    fullWidth
                    margin="none"
                    InputLabelProps={{ shrink: true }}
                    error={!!formErrors.net_salary}
                    helperText={formErrors.net_salary}
                    sx={{
                      width: "230px",
                      fontSize: "0.85rem",
                      "& .MuiInputBase-root": { minHeight: 35 },
                      "& .MuiInputLabel-root": { fontSize: "0.8rem" },
                      "& legend": {
                        maxWidth: "60px",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} sx={{ width: "230px" }}></Grid>
              </Grid>

              <Grid
                container
                spacing={1}
                justifyContent="center"
                sx={{ width: "100%", ml: 2, mr: 1, px: 1, p: 1, mt: 1 }}
              >
                <Grid item xs={12} sx={{ width: "100%" }}>
                  <TextField
                    label="Remarks"
                    name="remarks"
                    type="text"
                    value={formData.remarks || ""}
                    onChange={handleChange}
                    margin="none"
                    multiline
                    minRows={3}
                    maxRows={3}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      fontSize: "0.85rem",
                      "& .MuiInputBase-root": {
                        width: "100%",
                        alignItems: "flex-start",
                        paddingLeft: "0 !important",
                      },
                      "& textarea": {
                        overflowY: "auto",
                        resize: "none",
                        padding: "6px 8px",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.85rem",
                        textAlign: "left",
                        padding: "0 !important",
                      },
                      "& legend": {
                        maxWidth: "50px",
                      },
                      margin: 0,
                      padding: 0,
                    }}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button
                onClick={handleDialogClose}
                variant="outlined"
                color="inherit"
                sx={{ mr: 1, color: "#555", borderColor: "#ccc" }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                color="primary"
                sx={{
                  ...(isEditing && {
                    backgroundColor: "#4CAF50",
                    "&:hover": { backgroundColor: "#45a049" },
                  }),
                }}
              >
                {isEditing ? "Update" : "Save"}
              </Button>
            </DialogActions>
          </Dialog>
        </ThemeProvider>
      </Box>
    </MainLayout>
  );
}

export default EmployeeSalary;
